/**
 * BTK - shared unit DIMENSION filter helpers
 * ------------------------------------------------------------------
 * Width / Depth / Height are not product-level facets on a kitchen
 * unit - they are VARIANT OPTIONS inside each product. So a "filter"
 * on these axes does two things at once:
 *
 *   1. PRESELECT - every card that sells the chosen value is switched
 *      to it (price, image and Add button follow).
 *   2. FILTER    - every card that does NOT sell it is hidden.
 *
 * Both the kitchen-units listing page and the planner's Add Units
 * popup do exactly that, over the same three axes, with the same
 * ordering and the same Standard/Reduced `[split]` convention. This
 * file is the one copy of that shared vocabulary.
 *
 * Deliberately a PLAIN script (window.BTKUnitDims), not an ES module:
 * the planner's module graph needs every importer of a shared
 * sub-module to have its ?v= bumped together, and this is read at
 * call time by both surfaces instead. Nothing here touches the DOM -
 * each surface renders its own pills in its own house style.
 */
(function () {
  'use strict';

  // The only axes that filter. Order is the render order.
  var AXES = ['width', 'depth', 'height'];

  // Wall + tall units sell Height as named sizes, never millimetres
  // (see HEIGHT_LABELS_BY_CATEGORY in the planner catalogue for the mm
  // each maps to - deliberately NOT shown to the customer here). Sorted
  // small -> large; alphabetically "High" would sort first, which reads
  // as nonsense on a size control.
  var HEIGHT_ORDER = ['low', 'medium', 'med', 'high'];

  // `Depth [split]` renders as two columns. Which value counts as the
  // Standard one is the listing-page convention from
  // snippets/btk-product-card.liquid - kept byte-identical here.
  function valueHandle(v) {
    return String(v == null ? '' : v).toLowerCase().replace(/\s+/g, '-');
  }

  function isStandardValue(v) {
    var h = valueHandle(v);
    return h === '570' || h === 'medium';
  }

  // "Depth [split]" -> { label: 'Depth', hint: 'split' }
  function parseOptionName(raw) {
    var s = String(raw == null ? '' : raw);
    var i = s.indexOf('[');
    if (i === -1) return { label: s.trim(), hint: '' };
    var j = s.indexOf(']', i);
    return {
      label: s.slice(0, i).trim(),
      hint: (j === -1 ? s.slice(i + 1) : s.slice(i + 1, j)).trim().toLowerCase()
    };
  }

  // Which filter axis (if any) an option label belongs to. Anything
  // else - Side, Door config, hinge - is NOT a filter axis and returns
  // null, so a group that sells it simply gets no pill for it.
  function axisKey(label) {
    var l = String(label == null ? '' : label)
      .toLowerCase()
      .replace(/\[[^\]]*\]/g, '')
      .replace(/\([^)]*\)/g, '')
      .trim();
    for (var i = 0; i < AXES.length; i++) {
      if (l === AXES[i] || l.indexOf(AXES[i] + ' ') === 0) return AXES[i];
    }
    return null;
  }

  function numeric(v) {
    var n = parseInt(String(v == null ? '' : v), 10);
    return isNaN(n) ? null : n;
  }

  // Sort an axis's values into the order a customer expects.
  //   width / depth  - numeric ascending (300, 400, 500, 1000)
  //   height         - Low, Medium, High (or numeric if the group sells
  //                    millimetre heights, e.g. built-under housings)
  // Values we can't place keep their original relative order at the end,
  // so an unexpected label is never silently dropped or shuffled.
  function sortValues(axis, values) {
    var list = (values || []).slice();
    var order = new Map();
    list.forEach(function (v, i) { order.set(v, i); });

    function rank(v) {
      if (axis === 'height') {
        var idx = HEIGHT_ORDER.indexOf(valueHandle(v));
        if (idx !== -1) return idx;
      }
      var n = numeric(v);
      if (n != null) return n;
      return null;
    }

    return list.sort(function (a, b) {
      var ra = rank(a), rb = rank(b);
      if (ra == null && rb == null) return order.get(a) - order.get(b);
      if (ra == null) return 1;
      if (rb == null) return -1;
      if (ra !== rb) return ra - rb;
      return order.get(a) - order.get(b);
    });
  }

  // Header for the non-standard column of a `[split]` row. Mirrors
  // btk-product-card.liquid: depth reduces, anything else is "Alternatives".
  function altHeader(axis) {
    return axis === 'depth' ? 'Reduced' : 'Alternatives';
  }

  // Human label for an axis, used when a surface has no rendered label
  // of its own to reuse.
  function axisLabel(axis) {
    return axis ? axis.charAt(0).toUpperCase() + axis.slice(1) : '';
  }

  // What a NAMED height is in millimetres (carcase height, plinth excluded),
  // per height family. The size pills stay named on purpose (see
  // HEIGHT_ORDER above); these numbers are for the "More details" popup
  // (btk-option-help.js), whose whole job is to say what "Medium" means.
  //
  // MIRRORS HEIGHT_LABELS_BY_CATEGORY in btk-kitchen-planner-engine-catalogue.js,
  // which drives the planner's geometry. Keep the two in step - if they ever
  // disagree, the popup describes a unit the planner draws differently.
  var HEIGHT_MM = {
    wall: { low: 575,  medium: 720,  med: 720,  high: 900  },
    tall: { low: 1825, medium: 1970, med: 1970, high: 2150 }
  };

  // Where the TOP of a unit sits, measured from the floor, for a wall unit -
  // the mount line it hangs from. Each is the finished top of the tall unit of
  // the same name (carcase + 150mm plinth), which is why same-name heights
  // line up across a kitchen.
  //
  // MIRRORS WALL_MOUNT_TOPS in btk-kitchen-planner-engine-wall-mount.js, which
  // is where the planner actually hangs wall units. Keep the two in step.
  var WALL_TOP_MM = { low: 1975, medium: 2120, med: 2120, high: 2300 };

  // ---- Unit FAMILIES and their GENERAL size ranges -------------------------
  // The "More details" popup is general information: it shows the range a unit
  // TYPE comes in, never the clicked product's own options - a wall unit sold
  // in one height still gets all three. Everything here is keyed off the
  // unit_type's category + group handles only.

  // Height family:
  //   wall / tall - named Low / Medium / High (HEIGHT_MM). Same rule as
  //                 heightLabelMap in the planner catalogue; appliance-housings
  //                 is MIXED, so its wall group is wall and the rest tall.
  //   topbox      - bridging / top box units. In the wall-units category but
  //                 sold in millimetres, so Low / Medium / High would be wrong.
  //   null        - no height range to explain.
  function heightFamily(categoryHandle, groupHandle) {
    var c = String(categoryHandle || ''), g = String(groupHandle || '');
    if (g === 'bridging-top-box-units' || /^bridging/.test(c)) return 'topbox';
    if (c === 'wall-units') return 'wall';
    if (c === 'tall-units') return 'tall';
    if (c === 'appliance-housings') return /wall/.test(g) ? 'wall' : 'tall';
    return null;
  }

  // Top box / bridging heights sold across the range - measured over every
  // unit product 2026-09-18 (500 is on only three products).
  var TOPBOX_HEIGHTS_MM = [290, 360, 455, 495, 500];

  // Depth family - wall-hung, or standing on legs (base, tall, corner base,
  // floor housings). Base and tall share ONE family on purpose.
  function depthFamily(categoryHandle, groupHandle) {
    var c = String(categoryHandle || ''), g = String(groupHandle || '');
    if (/^(wall|bridging)/.test(c) || c.indexOf('glass-wall') === 0) return 'wall';
    if (c === 'appliance-housings') return /wall/.test(g) ? 'wall' : 'legs';
    if (c === 'tall-units' || c === 'base-units' || c === 'corner-base-units') return 'legs';
    return null;
  }

  // The GENERAL carcase depths, for the "More details" popup. General info:
  // every unit with legs gets the full list, whether or not its own group is
  // sold at every depth (no tall unit is sold at 470, it still shows).
  // WALL units have a Standard depth (330) and NO reduced range: Standard /
  // Reduced is only a thing for units with legs (see depthSplits).
  var DEPTH_RANGE = {
    legs: { standard: 570, reduced: [470, 300] },
    wall: { standard: 330, reduced: [] }
  };
  function depthRange(family) { return DEPTH_RANGE[family] || null; }

  // Does this unit's Depth get the Standard / Reduced split? Only units WITH
  // LEGS - a plinth under them: base, tall, corner base, floor housings. A
  // wall-hung unit's depth is a plain row: 330 is simply its depth, and the
  // wall units sold at 300 (True Handleless, curved) or 570 (bridging) are not
  // "reduced" versions of it. The listing card and product page apply the
  // same rule in Liquid (unit_type plinth_height_mm > 0).
  function depthSplits(plinthMm) {
    return (Number(plinthMm) || 0) > 0;
  }

  // Millimetres for one height value: a numeric value is taken as-is, a named
  // one is looked up in its family. null when it can't be sized.
  function heightMm(value, family) {
    var n = numeric(value);
    if (n != null) return n;
    var map = HEIGHT_MM[family];
    return map ? (map[valueHandle(value)] || null) : null;
  }

  // The heights a family's popup lists, in order - names for wall / tall,
  // millimetres for top boxes - or [] when there is no range to show.
  function heightRange(family) {
    if (HEIGHT_MM[family]) return ['Low', 'Medium', 'High'];
    if (family === 'topbox') return TOPBOX_HEIGHTS_MM.map(String);
    return [];
  }

  // Floor to the top of the unit, for one height value.
  //   wall unit      - its mount line (WALL_TOP_MM). A numeric wall height has
  //                    no defined mount line, so null.
  //   floor-standing - carcase height + plinth; null without a plinth figure.
  function topFromFloorMm(value, family, plinthMm) {
    if (family === 'wall') {
      return numeric(value) == null ? (WALL_TOP_MM[valueHandle(value)] || null) : null;
    }
    var mm = heightMm(value, family);
    var p = Number(plinthMm) || 0;
    return (mm != null && p > 0) ? mm + p : null;
  }

  window.BTKUnitDims = {
    AXES: AXES.slice(),
    HEIGHT_ORDER: HEIGHT_ORDER.slice(),
    parseOptionName: parseOptionName,
    axisKey: axisKey,
    axisLabel: axisLabel,
    valueHandle: valueHandle,
    isStandardValue: isStandardValue,
    sortValues: sortValues,
    altHeader: altHeader,
    numeric: numeric,
    heightFamily: heightFamily,
    heightMm: heightMm,
    topFromFloorMm: topFromFloorMm,
    heightRange: heightRange,
    depthFamily: depthFamily,
    depthRange: depthRange,
    depthSplits: depthSplits
  };
})();
